import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import AiOverview, { DAY_PERIODS } from './AiOverview';
import { getAiMetrics, getAiUserCounts, listAiUsers } from '../../actions/aiChatAction';
import { istDayRange, resolveDayRange, resolveTrendRange, toApiDate } from '../../helpers/dates';
import { setAIUserList } from '../../reducers/aiChatSlice';
import './aichat.css';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * `today` is the IST calendar day: 00:00:00.000 to 23:59:59.999 IST, not a rolling
 * 24 hours. The other presets stay rolling windows ending now.
 *
 * Returns null for a custom range that is not filled in yet, so callers can leave
 * what is on screen alone rather than firing a request that cannot mean anything.
 */
function rangeFor(period, custom) {
  if (period === 'custom') {
    const resolved = resolveDayRange(custom);
    if (!resolved) return null;
    return {
      start_date: toApiDate(resolved.start),
      end_date: toApiDate(resolved.end),
      // Sent so a multi-month custom range does not build a series of hundreds of
      // daily entries that the tiles never read.
      bucket: resolved.bucket,
    };
  }
  if (period === 'today') {
    const { start, end } = istDayRange();
    return { start_date: toApiDate(start), end_date: toApiDate(end) };
  }
  const end = new Date();
  const days = DAY_PERIODS.find((p) => p.value === period)?.days ?? 1;
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return { start_date: toApiDate(start), end_date: toApiDate(end) };
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const VIEWS = [
  { key: 'all', label: 'All users' },
  { key: 'active_today', label: 'Active today', attention: true },
  { key: 'paying', label: 'Paying users' },
  { key: 'low_credits', label: 'Low credits' },
];

export default function AiChatList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dayPeriod, setDayPeriod] = useState('24h');
  const [dayCustom, setDayCustom] = useState({ start: '', end: '' });
  const [metrics, setMetrics] = useState(null);
  const [trend, setTrend] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  // The trend runs on its own window, independent of the day period driving the tiles.
  const [trendRange, setTrendRange] = useState('30d');
  const [trendCustom, setTrendCustom] = useState({ start: '', end: '' });
  const [trendLoading, setTrendLoading] = useState(true);

  /* Held as state rather than derived, so a half-entered custom range keeps the
     previous window on screen instead of blanking the tiles and the table between
     picking a start date and picking an end date. */
  const [range, setRange] = useState(() => rangeFor('24h'));

  React.useEffect(() => {
    const next = rangeFor(dayPeriod, dayCustom);
    if (next) setRange(next);
  }, [dayPeriod, dayCustom]);

  // Tiles follow the day period.
  React.useEffect(() => {
    let cancelled = false;
    setMetricsLoading(true);
    getAiMetrics(range)
      .then((data) => {
        if (!cancelled) setMetrics(data);
      })
      .catch(() => {
        if (!cancelled) setMetrics(null);
      })
      .finally(() => {
        if (!cancelled) setMetricsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  // The trend chart follows its own range selector. Bucket comes with the range: a
  // year drawn one day at a time is 365 unreadable columns.
  const trendWindow = useMemo(
    () => resolveTrendRange(trendRange, trendCustom),
    [trendRange, trendCustom]
  );

  React.useEffect(() => {
    // An unfinished custom range resolves to null; keep the last chart until it is.
    if (!trendWindow) return undefined;
    let cancelled = false;
    setTrendLoading(true);
    getAiMetrics({
      start_date: toApiDate(trendWindow.start),
      end_date: toApiDate(trendWindow.end),
      bucket: trendWindow.bucket,
    })
      .then((data) => {
        if (!cancelled) setTrend(data);
      })
      .catch(() => {
        if (!cancelled) setTrend(null);
      })
      .finally(() => {
        if (!cancelled) setTrendLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [trendWindow]);

  const fetcher = useCallback(
    async (params) => {
      const res = await listAiUsers({ ...range, ...params });
      // Cached for the transcript screen's prev/next, which walks the page the
      // admin is currently looking at.
      dispatch(setAIUserList(res?.items || []));
      return res;
    },
    [dispatch, range]
  );

  const list = useListState({
    views: VIEWS,
    fetcher,
    countsFetcher: getAiUserCounts,
    // Last activity is the priority chain: most recent chat, else last purchase,
    // else signup. Sorting on it puts anyone who did anything recently on top.
    initialSort: { column: 'activity_at', direction: 'desc' },
    limit: 25,
  });

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: 230,
        filter: 'text',
        // Hiding the name would leave a table of numbers belonging to nobody.
        hideable: false,
        render: (row) => (
          <UserCell
            name={row.properties?.user_name || row.email || 'Unknown'}
            secondary={row.email}
            seed={row.user_id}
          />
        ),
      },
      {
        key: 'phone_number',
        header: 'Cell',
        width: 150,
        mono: true,
        filter: 'text',
        // Almost always blank on AI accounts, so it is off until asked for.
        defaultHidden: true,
        render: (row) =>
          row.phone_number?.number
            ? `${row.phone_number.country_code || ''} ${row.phone_number.number}`.trim()
            : '—',
      },
      {
        key: 'transaction_count',
        header: 'Transactions',
        width: 140,
        mono: true,
        align: 'right',
        sortable: true,
        filter: 'number',
      },
      {
        key: 'purchase_value',
        header: 'Purchase value',
        width: 140,
        mono: true,
        align: 'right',
        sortable: true,
        filter: 'number',
        render: (row) => inr(row.purchase_value),
      },
      {
        // Shows flows and credits together, but only one of them can be filtered on;
        // credits is the number an admin chases, so the menu targets that.
        key: 'balance',
        header: 'Flow / credits',
        width: 130,
        mono: true,
        filter: 'number',
        filterKey: 'available_credits',
        render: (row) =>
          `${row.ai_access?.flash_available ?? 0} / ${row.ai_access?.available_credits ?? 0}`,
      },
      {
        // Replaces the Created at column: activity_at falls back to signup time, so
        // for a brand-new account this shows exactly what Created at used to.
        key: 'activity_at',
        header: 'Last activity',
        width: 160,
        muted: true,
        sortable: true,
        filter: 'date',
        render: (row) => formatDateTime(row.activity_at),
      },
      {
        key: 'latest_message_timestamp',
        header: 'Latest message',
        width: 160,
        muted: true,
        sortable: true,
        filter: 'date',
        render: (row) => formatDateTime(row.latest_message_timestamp),
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="AI"
        title="AI Chat"
        subtitle="Everyone using the AI astrologer, with purchase history and transcripts."
      />

      <AiOverview
        metrics={metrics}
        trend={trend}
        loading={metricsLoading}
        dayPeriod={dayPeriod}
        onDayPeriodChange={setDayPeriod}
        dayCustom={dayCustom}
        onDayCustomChange={setDayCustom}
        trendRange={trendRange}
        onTrendRangeChange={setTrendRange}
        trendCustom={trendCustom}
        onTrendCustomChange={setTrendCustom}
        trendBucket={trendWindow?.bucket}
        trendLoading={trendLoading}
        // Clicking a bar answers "who is behind this day's revenue" by filtering the
        // table to everyone who bought that day. Not the same as the Last activity
        // column: a repeat buyer belongs to every day they purchased on.
        onSelectDay={(date) => list.setColumnFilter('transacted_on', { op: 'eq', value: date })}
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(row) => row.user_id}
        noun="users"
        // Search matched name, email and phone, which the column filters now do
        // precisely and per column.
        searchable={false}
        // transacted_on has no column of its own -- it comes from clicking a bar on
        // the trend chart -- so the chip needs to be told what to call it.
        filterLabels={{ transacted_on: { label: 'Purchased', kind: 'date' } }}
        // Filtering lives in the column headers now. The three dropdowns that used to
        // sit here were never wired to anything -- the API ignored the parameters, so
        // choosing "5+" changed nothing.
        onRowClick={(row) => navigate(`/ai-chat/${row.user_id}`)}
        empty={{
          icon: 'bx-bot',
          title: 'No AI users in this period',
          description: 'Widen the day period to see users who bought earlier.',
        }}
      />
    </>
  );
}
